/**
 * Session state: the undo stack (the "light escape hatch") and crash-safe
 * IndexedDB autosave. The history surface is intentionally tiny —
 * undo-last-op and clear, nothing more. No selection, no per-shape editing.
 */
import type { Scene, Shape } from './scene';

export type UndoOp = { type: 'commit'; ids: string[] } | { type: 'clear'; shapes: Shape[] };

const DB_NAME = 'alchemy';
const DB_VERSION = 2;
export const SESSION_STORE = 'sessions';
export const LIBRARY_STORE = 'library';
// Embed mode (?embed — the portfolio's inline canvas) gets its own session
// slot so scribbles there never overwrite the full app's work-in-progress.
const KEY =
  typeof location !== 'undefined' && new URLSearchParams(location.search).has('embed')
    ? 'embed'
    : 'current';
const MAX_UNDO = 200;

let dbPromise: Promise<IDBDatabase | null> | null = null;

/**
 * Shared IndexedDB opener at version 2. Creates the 'sessions' and 'library'
 * stores if missing — so bumping the version never clobbers existing scenes,
 * and either subsystem may open the DB first. Returns null on any failure.
 */
export function openAlchemyDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(SESSION_STORE)) db.createObjectStore(SESSION_STORE);
        if (!db.objectStoreNames.contains(LIBRARY_STORE)) db.createObjectStore(LIBRARY_STORE);
      };
      req.onsuccess = () => {
        const db = req.result;
        // Release this connection if another tab needs a newer schema version.
        db.onversionchange = () => {
          db.close();
          dbPromise = null;
        };
        resolve(db);
      };
      req.onerror = () => {
        dbPromise = null; // allow a later retry instead of caching the failure
        resolve(null);
      };
      // onblocked: leave the request pending so onsuccess can still fire once the
      // older connection closes — resolving null here would kill persistence forever.
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

export class Session {
  private stack: UndoOp[] = [];
  private saveTimer: ReturnType<typeof setTimeout> | null = null;

  pushCommit(ids: string[]): void {
    this.stack.push({ type: 'commit', ids });
    this.cap();
  }

  pushClear(shapes: Shape[]): void {
    this.stack.push({ type: 'clear', shapes });
    this.cap();
  }

  undo(): UndoOp | null {
    return this.stack.pop() ?? null;
  }

  canUndo(): boolean {
    return this.stack.length > 0;
  }

  private cap(): void {
    if (this.stack.length > MAX_UNDO) this.stack.splice(0, this.stack.length - MAX_UNDO);
  }

  /** Debounced autosave of the current scene. */
  save(scene: Scene): void {
    if (this.saveTimer !== null) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => void this.writeNow(scene), 400);
  }

  private async writeNow(scene: Scene): Promise<void> {
    const db = await openAlchemyDb();
    if (!db) return;
    const data = {
      version: 1,
      width: scene.width,
      height: scene.height,
      background: scene.background,
      palette: scene.palette,
      activeInk: scene.activeInk,
      shapes: scene.shapes,
    };
    try {
      const tx = db.transaction(SESSION_STORE, 'readwrite');
      tx.objectStore(SESSION_STORE).put(data, KEY);
    } catch {
      /* best effort */
    }
  }

  async load(): Promise<Scene | null> {
    const db = await openAlchemyDb();
    if (!db) return null;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(SESSION_STORE, 'readonly');
        const req = tx.objectStore(SESSION_STORE).get(KEY);
        req.onsuccess = () => {
          const v = req.result as Scene | undefined;
          resolve(v && Array.isArray(v.shapes) ? v : null);
        };
        req.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
  }
}
